// Ship class definitions and slot system
// Post-overhaul:
//   - Ships are named `{class} T{tier}` (e.g. corvette_t1). Legacy IDs (sparrow,
//     swift, ...) remain as aliases in SHIPS so existing DB rows keep working.
//   - Movement and shields are NATIVE. Engines and shields are no longer slots.
//     (ENGINE / SHIELD kept as valid SlotType enum values for back-compat but no
//     ship has those slots in its layout anymore.)
//   - New stats: powerCapacity (fitting cap), heatCapacity, heatDissipation,
//     shieldCoverage, shieldExpansion, shieldHeatCost, shieldUpkeep, shieldRadius.

export type ShipTier = 1 | 2 | 3 | 4;

export type ShipClassType =
  | 'corvette'
  | 'frigate'
  | 'destroyer'
  | 'cruiser'
  | 'battleship'
  | 'carrier'
  | 'dreadnought'
  | 'hauler';

// ENGINE/SHIELD kept for back-compat with consumer code that switches on enum.
export type SlotType = 'ENGINE' | 'WEAPON' | 'TOOL' | 'SHIELD' | 'ENHANCER' | 'UTILITY';

export type SlotSize = 'SMALL' | 'MEDIUM' | 'LARGE';

export interface SlotDefinition {
  type: SlotType;
  size: SlotSize;
  position: 'front' | 'back' | 'left' | 'right' | 'center' | 'top' | 'bottom';
  equipped?: string;
}

export interface ShipStats {
  // Movement (native)
  baseAcceleration: number;
  baseMaxSpeed: number;
  rotationSpeed: number;
  brakingPower: number;
  baseStrafeAcceleration: number;

  // Durability
  hull: number;
  mass: number;

  // Fitting budget
  powerCapacity: number;   // cap on sum(equipment.power)
  /** @deprecated Identical to powerCapacity. Use powerCapacity instead. */
  basePower: number;

  // Heat (combat budget)
  heatCapacity: number;    // when reached, ship overloads
  heatDissipation: number; // heat / second passive

  // Shield (native)
  shieldCoverage: number;     // degrees of shield arc
  shieldExpansion: number;    // ms to fully raise
  shieldHeatCost: number;     // multiplier: heat = damage × shieldHeatCost
  shieldUpkeep: number;       // heat/sec while active (scaled by coverage)
  shieldRadius: number;       // base shield projection radius (world units)

  // Legacy / native stat compat
  shield: number;             // legacy field — 0 in new model (shield is HP-less)

  // Cargo
  cargoCapacity: number;

  // Other
  sensorRange: number;
  jumpRange: number;
}

export type ShipSize = 'small' | 'medium' | 'large';

export interface ShipClass {
  id: string;
  name: string;
  description: string;
  tier: ShipTier;
  class: ShipClassType;
  category: 'starter' | 'explorer' | 'mining' | 'combat' | 'utility' | 'advanced';
  size: ShipSize;
  stats: ShipStats;
  slots: SlotDefinition[];
}

// ============================================
// Slot layout helpers
// Layouts are derived from class × tier with a simple rule per class.
// ============================================

interface LayoutRecipe {
  weapons: Array<{ size: SlotSize; position: SlotDefinition['position'] }>;
  enhancers: Array<{ size: SlotSize; position: SlotDefinition['position'] }>;
  tools: Array<{ size: SlotSize; position: SlotDefinition['position'] }>;
  utilities: Array<{ size: SlotSize; position: SlotDefinition['position'] }>;
}

function buildSlots(recipe: LayoutRecipe): SlotDefinition[] {
  const slots: SlotDefinition[] = [];
  for (const w of recipe.weapons)   slots.push({ type: 'WEAPON',   size: w.size, position: w.position });
  for (const t of recipe.tools)     slots.push({ type: 'TOOL',     size: t.size, position: t.position });
  for (const e of recipe.enhancers) slots.push({ type: 'ENHANCER', size: e.size, position: e.position });
  for (const u of recipe.utilities) slots.push({ type: 'UTILITY',  size: u.size, position: u.position });
  return slots;
}

// Per-class × per-tier slot recipes. Tuned so:
//   - small ships (corvette/frigate/hauler) have few slots
//   - bigger ships (destroyer→dreadnought) have more slots and more LARGE
//   - capitals (carrier/dreadnought T3+) can fit Shield Amplifier (LARGE enhancer)
function recipe(cls: ShipClassType, tier: ShipTier): LayoutRecipe {
  switch (cls) {
    case 'corvette': {
      const sizesByTier: Array<{ wp: SlotSize[]; en: SlotSize[]; tl: SlotSize[]; ut: SlotSize[] }> = [
        { wp: ['SMALL'],                       en: ['SMALL','SMALL'],          tl: ['SMALL'], ut: ['SMALL'] },
        { wp: ['SMALL','SMALL'],               en: ['SMALL','SMALL'],          tl: ['SMALL'], ut: ['SMALL'] },
        { wp: ['MEDIUM','SMALL'],              en: ['MEDIUM','SMALL'],         tl: ['SMALL'], ut: ['SMALL'] },
        { wp: ['MEDIUM','SMALL','SMALL'],      en: ['MEDIUM','SMALL','SMALL'], tl: ['SMALL'], ut: ['SMALL','SMALL'] },
      ];
      const t = sizesByTier[tier - 1];
      return {
        weapons:   t.wp.map((s, i) => ({ size: s, position: (['front','center','left'] as const)[i] ?? 'right' })),
        enhancers: t.en.map((s, i) => ({ size: s, position: (['left','right','bottom'] as const)[i] ?? 'top' })),
        tools:     t.tl.map((s, i) => ({ size: s, position: (['top'] as const)[i] ?? 'bottom' })),
        utilities: t.ut.map((s, i) => ({ size: s, position: (['center'] as const)[i] ?? 'bottom' })),
      };
    }
    case 'frigate': {
      const sizesByTier = [
        { wp: ['SMALL','SMALL'],                   en: ['SMALL','SMALL'],                  tl: ['SMALL'], ut: ['SMALL'] },
        { wp: ['MEDIUM','SMALL'],                  en: ['SMALL','SMALL'],                  tl: ['SMALL'], ut: ['SMALL'] },
        { wp: ['MEDIUM','SMALL','SMALL'],          en: ['MEDIUM','SMALL'],                 tl: ['SMALL'], ut: ['SMALL','SMALL'] },
        { wp: ['MEDIUM','MEDIUM','SMALL'],         en: ['MEDIUM','SMALL','SMALL'],         tl: ['SMALL'], ut: ['SMALL','SMALL'] },
      ];
      const t = sizesByTier[tier - 1];
      return {
        weapons:   t.wp.map((s, i) => ({ size: s as SlotSize, position: (['front','center','left'] as const)[i] ?? 'right' })),
        enhancers: t.en.map((s, i) => ({ size: s as SlotSize, position: (['left','right','top'] as const)[i] ?? 'bottom' })),
        tools:     t.tl.map((s) => ({ size: s as SlotSize, position: 'top' as const })),
        utilities: t.ut.map((_, i) => ({ size: 'SMALL' as SlotSize, position: 'center' as const })),
      };
    }
    case 'destroyer': {
      const sizesByTier = [
        { wp: ['SMALL','SMALL'],                       en: ['SMALL','SMALL','SMALL'],                tl: ['SMALL'],         ut: ['SMALL','SMALL'] },
        { wp: ['MEDIUM','SMALL','SMALL'],              en: ['SMALL','SMALL','SMALL'],                tl: ['SMALL','SMALL'], ut: ['SMALL','SMALL'] },
        { wp: ['MEDIUM','SMALL','SMALL','SMALL'],      en: ['MEDIUM','SMALL','SMALL','SMALL'],       tl: ['SMALL','SMALL'], ut: ['SMALL','SMALL','SMALL'] },
        { wp: ['MEDIUM','MEDIUM','SMALL','SMALL'],     en: ['MEDIUM','MEDIUM','SMALL','SMALL'],      tl: ['SMALL','SMALL'], ut: ['SMALL','SMALL','SMALL'] },
      ];
      const t = sizesByTier[tier - 1];
      return {
        weapons:   t.wp.map((s, i) => ({ size: s as SlotSize, position: (['front','center','left','right'] as const)[i] ?? 'top' })),
        enhancers: t.en.map((s, i) => ({ size: s as SlotSize, position: (['left','right','top','bottom'] as const)[i] ?? 'center' })),
        tools:     t.tl.map((s, i) => ({ size: s as SlotSize, position: (['top','bottom'] as const)[i] ?? 'left' })),
        utilities: t.ut.map(() => ({ size: 'SMALL' as SlotSize, position: 'center' as const })),
      };
    }
    case 'cruiser': {
      const sizesByTier = [
        { wp: ['MEDIUM','SMALL','SMALL'],                  en: ['MEDIUM','SMALL','SMALL'],                  tl: ['SMALL'],         ut: ['SMALL','SMALL'] },
        { wp: ['MEDIUM','MEDIUM','SMALL'],                 en: ['MEDIUM','SMALL','SMALL','SMALL'],          tl: ['SMALL','SMALL'], ut: ['SMALL','SMALL'] },
        { wp: ['MEDIUM','MEDIUM','SMALL','SMALL'],         en: ['MEDIUM','MEDIUM','SMALL','SMALL'],         tl: ['SMALL','SMALL'], ut: ['SMALL','SMALL','SMALL'] },
        { wp: ['MEDIUM','MEDIUM','MEDIUM','SMALL'],        en: ['LARGE','MEDIUM','SMALL','SMALL'],          tl: ['SMALL','SMALL'], ut: ['SMALL','SMALL','SMALL'] },
      ];
      const t = sizesByTier[tier - 1];
      return {
        weapons:   t.wp.map((s, i) => ({ size: s as SlotSize, position: (['front','center','left','right'] as const)[i] ?? 'top' })),
        enhancers: t.en.map((s, i) => ({ size: s as SlotSize, position: (['top','left','right','bottom'] as const)[i] ?? 'center' })),
        tools:     t.tl.map((s, i) => ({ size: s as SlotSize, position: (['top','bottom'] as const)[i] ?? 'left' })),
        utilities: t.ut.map(() => ({ size: 'SMALL' as SlotSize, position: 'center' as const })),
      };
    }
    case 'battleship': {
      const sizesByTier = [
        { wp: ['MEDIUM','MEDIUM','SMALL','SMALL'],                       en: ['MEDIUM','SMALL','SMALL'],                                tl: ['SMALL'],         ut: ['SMALL','SMALL'] },
        { wp: ['MEDIUM','MEDIUM','SMALL','SMALL','SMALL'],               en: ['MEDIUM','MEDIUM','SMALL','SMALL'],                       tl: ['SMALL','SMALL'], ut: ['SMALL','SMALL'] },
        { wp: ['MEDIUM','MEDIUM','MEDIUM','SMALL','SMALL'],              en: ['LARGE','MEDIUM','SMALL','SMALL'],                        tl: ['SMALL','SMALL'], ut: ['SMALL','SMALL','SMALL'] },
        { wp: ['MEDIUM','MEDIUM','MEDIUM','MEDIUM','SMALL','SMALL'],     en: ['LARGE','MEDIUM','MEDIUM','SMALL','SMALL'],               tl: ['SMALL','SMALL'], ut: ['SMALL','SMALL','SMALL'] },
      ];
      const t = sizesByTier[tier - 1];
      return {
        weapons:   t.wp.map((s, i) => ({ size: s as SlotSize, position: (['front','center','left','right','top','bottom'] as const)[i] ?? 'center' })),
        enhancers: t.en.map((s, i) => ({ size: s as SlotSize, position: (['top','left','right','bottom','center'] as const)[i] ?? 'top' })),
        tools:     t.tl.map((s, i) => ({ size: s as SlotSize, position: (['top','bottom'] as const)[i] ?? 'left' })),
        utilities: t.ut.map(() => ({ size: 'SMALL' as SlotSize, position: 'center' as const })),
      };
    }
    case 'carrier': {
      const sizesByTier = [
        { wp: ['SMALL','SMALL','SMALL'],                       en: ['MEDIUM','SMALL','SMALL'],                       tl: ['SMALL'],         ut: ['SMALL','SMALL','SMALL'] },
        { wp: ['MEDIUM','SMALL','SMALL'],                      en: ['MEDIUM','MEDIUM','SMALL','SMALL'],              tl: ['SMALL','SMALL'], ut: ['SMALL','SMALL','SMALL'] },
        { wp: ['MEDIUM','MEDIUM','SMALL','SMALL'],             en: ['LARGE','MEDIUM','SMALL','SMALL'],               tl: ['SMALL','SMALL'], ut: ['SMALL','SMALL','SMALL','SMALL'] },
        { wp: ['MEDIUM','MEDIUM','MEDIUM','SMALL'],            en: ['LARGE','MEDIUM','MEDIUM','SMALL'],              tl: ['SMALL','SMALL'], ut: ['SMALL','SMALL','SMALL','SMALL'] },
      ];
      const t = sizesByTier[tier - 1];
      return {
        weapons:   t.wp.map((s, i) => ({ size: s as SlotSize, position: (['front','center','left','right'] as const)[i] ?? 'top' })),
        enhancers: t.en.map((s, i) => ({ size: s as SlotSize, position: (['top','left','right','bottom'] as const)[i] ?? 'center' })),
        tools:     t.tl.map((s, i) => ({ size: s as SlotSize, position: (['top','bottom'] as const)[i] ?? 'left' })),
        utilities: t.ut.map(() => ({ size: 'SMALL' as SlotSize, position: 'center' as const })),
      };
    }
    case 'dreadnought': {
      const sizesByTier = [
        { wp: ['MEDIUM','MEDIUM','MEDIUM','SMALL','SMALL'],                        en: ['MEDIUM','MEDIUM','SMALL','SMALL'],                       tl: ['SMALL'],         ut: ['SMALL','SMALL'] },
        { wp: ['MEDIUM','MEDIUM','MEDIUM','MEDIUM','SMALL','SMALL'],               en: ['LARGE','MEDIUM','SMALL','SMALL'],                        tl: ['SMALL','SMALL'], ut: ['SMALL','SMALL','SMALL'] },
        { wp: ['MEDIUM','MEDIUM','MEDIUM','MEDIUM','MEDIUM','SMALL','SMALL'],      en: ['LARGE','MEDIUM','MEDIUM','SMALL','SMALL'],               tl: ['SMALL','SMALL'], ut: ['SMALL','SMALL','SMALL'] },
        { wp: ['MEDIUM','MEDIUM','MEDIUM','MEDIUM','MEDIUM','MEDIUM','SMALL','SMALL'], en: ['LARGE','LARGE','MEDIUM','MEDIUM','SMALL'],            tl: ['SMALL','SMALL'], ut: ['SMALL','SMALL','SMALL','SMALL'] },
      ];
      const t = sizesByTier[tier - 1];
      return {
        weapons:   t.wp.map((s, i) => ({ size: s as SlotSize, position: (['front','center','left','right','top','bottom'] as const)[i] ?? 'center' })),
        enhancers: t.en.map((s, i) => ({ size: s as SlotSize, position: (['top','left','right','bottom','center'] as const)[i] ?? 'top' })),
        tools:     t.tl.map((s, i) => ({ size: s as SlotSize, position: (['top','bottom'] as const)[i] ?? 'left' })),
        utilities: t.ut.map(() => ({ size: 'SMALL' as SlotSize, position: 'center' as const })),
      };
    }
    case 'hauler': {
      const sizesByTier = [
        { wp: ['SMALL'],                              en: ['MEDIUM','SMALL','SMALL'],                             tl: [],            ut: ['SMALL'] },
        { wp: ['SMALL','SMALL'],                      en: ['MEDIUM','MEDIUM','SMALL'],                            tl: [],            ut: ['SMALL','SMALL'] },
        { wp: ['SMALL','SMALL'],                      en: ['MEDIUM','MEDIUM','SMALL','SMALL'],                    tl: [],            ut: ['SMALL','SMALL'] },
        { wp: ['SMALL','SMALL','SMALL'],              en: ['MEDIUM','MEDIUM','MEDIUM','SMALL'],                   tl: [],            ut: ['SMALL','SMALL'] },
      ];
      const t = sizesByTier[tier - 1];
      return {
        weapons:   t.wp.map((s, i) => ({ size: s as SlotSize, position: (['front','center','left'] as const)[i] ?? 'right' })),
        enhancers: t.en.map((s, i) => ({ size: s as SlotSize, position: (['top','left','right','bottom'] as const)[i] ?? 'center' })),
        tools:     [],
        utilities: t.ut.map(() => ({ size: 'SMALL' as SlotSize, position: 'center' as const })),
      };
    }
  }
}

// ============================================
// Per-class × per-tier stat tables
// (Verbatim from the overhaul spec.)
// ============================================

const POWER_CAP: Record<ShipClassType, [number, number, number, number]> = {
  corvette:    [80, 110, 150, 200],
  frigate:     [110, 150, 200, 270],
  destroyer:   [160, 220, 290, 380],
  cruiser:     [200, 270, 360, 470],
  battleship:  [280, 370, 490, 640],
  carrier:     [230, 300, 400, 520],
  dreadnought: [370, 490, 650, 850],
  hauler:      [130, 170, 225, 290],
};

const HEAT_CAP: Record<ShipClassType, [number, number, number, number]> = {
  corvette:    [80, 95, 110, 130],
  frigate:     [100, 120, 140, 165],
  destroyer:   [130, 155, 180, 210],
  cruiser:     [140, 170, 200, 235],
  battleship:  [180, 215, 250, 295],
  carrier:     [160, 190, 225, 265],
  dreadnought: [220, 260, 305, 360],
  hauler:      [90, 110, 130, 155],
};

const HEAT_DIS: Record<ShipClassType, [number, number, number, number]> = {
  corvette:    [12, 14, 16, 19],
  frigate:     [11, 13, 15, 18],
  destroyer:   [9, 11, 13, 15],
  cruiser:     [9, 11, 13, 15],
  battleship:  [7, 9, 11, 13],
  carrier:     [8, 10, 12, 14],
  dreadnought: [6, 8, 10, 12],
  hauler:      [10, 12, 14, 16],
};

// Shield natives per class
const SHIELD_COVERAGE: Record<ShipClassType, number> = {
  corvette: 90, frigate: 90, destroyer: 180, cruiser: 180,
  battleship: 270, carrier: 270, dreadnought: 360, hauler: 90,
};

const SHIELD_EXPANSION: Record<ShipClassType, number> = {
  corvette: 200, frigate: 300, destroyer: 500, cruiser: 600,
  battleship: 900, carrier: 900, dreadnought: 1200, hauler: 700,
};

// Per-class shield upkeep base (heat/s, scaled by coverage in the formula
// `upkeep = base × (coverage / 90)`).
//
// 2026-05: battleship/carrier/dreadnought lowered so that idle shield-up
// doesn't auto-cook the ship. With previous values, a dread T1 paid +10 heat/s
// of upkeep alone vs only 6/s dissipation — it overloaded itself by holding
// shield up for 22s without taking any damage. Big capitals are meant to die
// to swarms, not to their own shield. Smaller classes (destroyer/cruiser/frigate/
// corvette) were already net-negative at rest and untouched.
const SHIELD_UPKEEP_BASE: Record<ShipClassType, number> = {
  corvette: 0.4, frigate: 0.7, destroyer: 2.0, cruiser: 2.0,
  battleship: 1.5, carrier: 1.5, dreadnought: 1.0, hauler: 0.7,
};

// Shield Heat Cost per class × tier: heat = damage × this
const SHIELD_HEAT_COST: Record<ShipClassType, [number, number, number, number]> = {
  corvette:    [1.0, 0.85, 0.75, 0.65],
  frigate:     [0.95, 0.8, 0.7, 0.6],
  destroyer:   [0.8, 0.65, 0.55, 0.45],
  cruiser:     [0.75, 0.6, 0.5, 0.4],
  battleship:  [0.7, 0.55, 0.45, 0.35],
  carrier:     [0.75, 0.6, 0.5, 0.4],
  dreadnought: [0.6, 0.45, 0.35, 0.25],
  hauler:      [1.1, 0.95, 0.85, 0.75],
};

// Base movement/durability/cargo by class × tier
interface MoveDurStats {
  accel: number; speed: number; rot: number; brake: number; strafe: number;
  hull: number; mass: number; cargo: number; sensor: number; jump: number;
  shieldRadius: number;
}

const MOVE_DUR: Record<ShipClassType, MoveDurStats[]> = {
  // Shield radii sized to cover SVG sprite visual (half-diagonal + ~15% margin).
  // SVG dimensions after -90° rotation: gameH = svgW*scale, gameW = svgH*scale.
  // corvette: 166×218@0.20 → 33h×44w, halfDiag≈28 → radius 35-45
  corvette: [
    { accel: 80, speed: 180, rot: 170, brake: 140, strafe: 60, hull: 80, mass: 70, cargo: 20, sensor: 500, jump: 0, shieldRadius: 35 },
    { accel: 86, speed: 190, rot: 178, brake: 148, strafe: 65, hull: 104, mass: 75, cargo: 25, sensor: 550, jump: 200, shieldRadius: 38 },
    { accel: 92, speed: 200, rot: 186, brake: 155, strafe: 70, hull: 136, mass: 80, cargo: 30, sensor: 600, jump: 350, shieldRadius: 42 },
    { accel: 98, speed: 210, rot: 195, brake: 162, strafe: 75, hull: 176, mass: 85, cargo: 40, sensor: 700, jump: 500, shieldRadius: 45 },
  ],
  // frigate: 162×224@0.35 → 57h×78w, halfDiag≈49 → radius 55-70
  frigate: [
    { accel: 70, speed: 160, rot: 145, brake: 120, strafe: 52, hull: 120, mass: 95, cargo: 25, sensor: 450, jump: 0, shieldRadius: 55 },
    { accel: 76, speed: 168, rot: 152, brake: 126, strafe: 56, hull: 156, mass: 100, cargo: 32, sensor: 500, jump: 250, shieldRadius: 60 },
    { accel: 81, speed: 176, rot: 160, brake: 132, strafe: 60, hull: 204, mass: 110, cargo: 40, sensor: 650, jump: 400, shieldRadius: 65 },
    { accel: 86, speed: 185, rot: 168, brake: 138, strafe: 64, hull: 264, mass: 120, cargo: 50, sensor: 800, jump: 550, shieldRadius: 70 },
  ],
  // destroyer: 196×164@0.50 → 98h×82w, halfDiag≈64 → radius 70-85
  destroyer: [
    { accel: 55, speed: 130, rot: 110, brake: 100, strafe: 40, hull: 200, mass: 140, cargo: 40, sensor: 400, jump: 0, shieldRadius: 70 },
    { accel: 59, speed: 137, rot: 116, brake: 105, strafe: 43, hull: 260, mass: 150, cargo: 50, sensor: 450, jump: 280, shieldRadius: 75 },
    { accel: 63, speed: 144, rot: 122, brake: 110, strafe: 46, hull: 340, mass: 165, cargo: 65, sensor: 500, jump: 380, shieldRadius: 80 },
    { accel: 67, speed: 150, rot: 128, brake: 115, strafe: 49, hull: 440, mass: 180, cargo: 85, sensor: 600, jump: 500, shieldRadius: 85 },
  ],
  // cruiser: 296×211@0.55 → 163h×116w, halfDiag≈100 → radius 110-130
  cruiser: [
    { accel: 50, speed: 120, rot: 100, brake: 95, strafe: 38, hull: 240, mass: 160, cargo: 50, sensor: 500, jump: 300, shieldRadius: 110 },
    { accel: 54, speed: 126, rot: 105, brake: 100, strafe: 41, hull: 312, mass: 170, cargo: 65, sensor: 550, jump: 400, shieldRadius: 115 },
    { accel: 58, speed: 132, rot: 110, brake: 105, strafe: 44, hull: 408, mass: 185, cargo: 85, sensor: 650, jump: 500, shieldRadius: 120 },
    { accel: 61, speed: 138, rot: 115, brake: 110, strafe: 47, hull: 528, mass: 200, cargo: 110, sensor: 800, jump: 650, shieldRadius: 130 },
  ],
  // battleship: 275×222@1.10 → 302h×244w, halfDiag≈195 → radius 210-240
  battleship: [
    { accel: 38, speed: 95, rot: 80, brake: 78, strafe: 28, hull: 500, mass: 280, cargo: 60, sensor: 500, jump: 350, shieldRadius: 210 },
    { accel: 41, speed: 100, rot: 84, brake: 82, strafe: 30, hull: 650, mass: 300, cargo: 80, sensor: 550, jump: 450, shieldRadius: 220 },
    { accel: 44, speed: 105, rot: 88, brake: 86, strafe: 32, hull: 845, mass: 325, cargo: 100, sensor: 650, jump: 550, shieldRadius: 230 },
    { accel: 47, speed: 110, rot: 92, brake: 90, strafe: 34, hull: 1100, mass: 355, cargo: 130, sensor: 800, jump: 700, shieldRadius: 240 },
  ],
  // carrier: 251×253@1.40 → 351h×354w, halfDiag≈250 → radius 270-310
  carrier: [
    { accel: 36, speed: 90, rot: 75, brake: 75, strafe: 27, hull: 420, mass: 310, cargo: 80, sensor: 600, jump: 400, shieldRadius: 270 },
    { accel: 39, speed: 95, rot: 79, brake: 79, strafe: 29, hull: 546, mass: 335, cargo: 105, sensor: 650, jump: 500, shieldRadius: 280 },
    { accel: 42, speed: 100, rot: 83, brake: 83, strafe: 31, hull: 710, mass: 365, cargo: 135, sensor: 750, jump: 600, shieldRadius: 295 },
    { accel: 44, speed: 105, rot: 87, brake: 87, strafe: 33, hull: 925, mass: 400, cargo: 175, sensor: 900, jump: 750, shieldRadius: 310 },
  ],
  // dreadnought: 285×196@2.00 → 570h×392w, halfDiag≈346 → radius 370-410
  dreadnought: [
    { accel: 25, speed: 70, rot: 60, brake: 62, strafe: 18, hull: 900, mass: 450, cargo: 80, sensor: 600, jump: 350, shieldRadius: 370 },
    { accel: 28, speed: 75, rot: 64, brake: 66, strafe: 20, hull: 1170, mass: 485, cargo: 105, sensor: 700, jump: 450, shieldRadius: 380 },
    { accel: 31, speed: 80, rot: 68, brake: 70, strafe: 22, hull: 1520, mass: 525, cargo: 135, sensor: 800, jump: 550, shieldRadius: 395 },
    { accel: 34, speed: 85, rot: 72, brake: 74, strafe: 24, hull: 1980, mass: 575, cargo: 175, sensor: 950, jump: 700, shieldRadius: 410 },
  ],
  // hauler: 191×190@0.30 → 57h×57w, halfDiag≈40 → radius 45-55
  hauler: [
    { accel: 45, speed: 110, rot: 90, brake: 88, strafe: 32, hull: 160, mass: 180, cargo: 60, sensor: 400, jump: 200, shieldRadius: 45 },
    { accel: 48, speed: 115, rot: 94, brake: 92, strafe: 34, hull: 200, mass: 200, cargo: 85, sensor: 450, jump: 300, shieldRadius: 48 },
    { accel: 51, speed: 120, rot: 98, brake: 96, strafe: 36, hull: 255, mass: 225, cargo: 115, sensor: 500, jump: 400, shieldRadius: 52 },
    { accel: 54, speed: 125, rot: 102, brake: 100, strafe: 38, hull: 330, mass: 250, cargo: 150, sensor: 600, jump: 500, shieldRadius: 55 },
  ],
};

// Class metadata (display & semantics)
const CLASS_META: Record<ShipClassType, {
  shortName: string; size: ShipSize; description: string;
  categories: ['starter' | 'combat' | 'utility' | 'advanced', 'starter' | 'combat' | 'utility' | 'advanced', 'starter' | 'combat' | 'utility' | 'advanced', 'starter' | 'combat' | 'utility' | 'advanced'];
}> = {
  corvette:    { shortName: 'Corvette',    size: 'small',  description: 'Nimble scout. Fast, agile, fragile.',
                 categories: ['starter','starter','combat','advanced'] },
  frigate:     { shortName: 'Frigate',     size: 'small',  description: 'Fast attack craft. Strike hard, strike fast.',
                 categories: ['combat','combat','combat','advanced'] },
  destroyer:   { shortName: 'Destroyer',   size: 'medium', description: 'Multi-role escort. Maximum flexibility.',
                 categories: ['utility','combat','combat','advanced'] },
  cruiser:     { shortName: 'Cruiser',     size: 'medium', description: 'All-rounder. Explorer and combat platform.',
                 categories: ['utility','utility','combat','advanced'] },
  battleship:  { shortName: 'Battleship',  size: 'large',  description: 'Heavy combat vessel. Frontline brawler.',
                 categories: ['combat','combat','combat','advanced'] },
  carrier:     { shortName: 'Carrier',     size: 'large',  description: 'Drone platform. Projects force at range.',
                 categories: ['utility','utility','combat','advanced'] },
  dreadnought: { shortName: 'Dreadnought', size: 'large',  description: 'Capital ship. Walking arsenal.',
                 categories: ['combat','combat','combat','advanced'] },
  hauler:      { shortName: 'Hauler',      size: 'medium', description: 'Cargo transport. Trade route specialist.',
                 categories: ['utility','utility','utility','advanced'] },
};

function buildShipClass(cls: ShipClassType, tier: ShipTier): ShipClass {
  const tierIdx = tier - 1;
  const meta = CLASS_META[cls];
  const move = MOVE_DUR[cls][tierIdx];
  const id = `${cls}_t${tier}`;
  const size: ShipSize = (cls === 'cruiser' && tier >= 3) || (cls === 'frigate' && tier >= 3) || (cls === 'hauler' && tier >= 3)
    ? (tier >= 3 ? 'large' : meta.size)
    : meta.size;
  const powerCap = POWER_CAP[cls][tierIdx];
  return {
    id,
    name: `${meta.shortName} T${tier}`,
    description: meta.description,
    tier,
    class: cls,
    category: meta.categories[tierIdx],
    size,
    stats: {
      baseAcceleration: move.accel,
      baseMaxSpeed: move.speed,
      rotationSpeed: move.rot,
      brakingPower: move.brake,
      baseStrafeAcceleration: move.strafe,
      hull: move.hull,
      mass: move.mass,
      powerCapacity: powerCap,
      basePower: powerCap, // @deprecated — identical to powerCapacity, kept for back-compat
      heatCapacity: HEAT_CAP[cls][tierIdx],
      heatDissipation: HEAT_DIS[cls][tierIdx],
      shieldCoverage: SHIELD_COVERAGE[cls],
      shieldExpansion: SHIELD_EXPANSION[cls],
      shieldHeatCost: SHIELD_HEAT_COST[cls][tierIdx],
      shieldUpkeep: SHIELD_UPKEEP_BASE[cls] * (SHIELD_COVERAGE[cls] / 90),
      shieldRadius: move.shieldRadius,
      shield: 0, // legacy: no HP shield in new model
      cargoCapacity: move.cargo,
      sensorRange: move.sensor,
      jumpRange: move.jump,
    },
    slots: buildSlots(recipe(cls, tier)),
  };
}

// ============================================
// SHIP REGISTRY
// All 32 ships keyed by canonical `{class}_t{tier}` id, with legacy IDs
// (sparrow, swift, ...) aliased to the same entry so DB rows keep resolving.
// ============================================

const LEGACY_SHIP_IDS: Record<string, string> = {
  sparrow: 'corvette_t1', swift: 'corvette_t2', falcon: 'corvette_t3', peregrine: 'corvette_t4',
  corsair: 'frigate_t1', raider: 'frigate_t2', stalker: 'frigate_t3', phantom: 'frigate_t4',
  sentinel: 'destroyer_t1', guardian: 'destroyer_t2', warden: 'destroyer_t3', vindicator: 'destroyer_t4',
  pioneer: 'cruiser_t1', voyager: 'cruiser_t2', pathfinder: 'cruiser_t3', odyssey: 'cruiser_t4',
  bulwark: 'battleship_t1', fortress: 'battleship_t2', juggernaut: 'battleship_t3', leviathan: 'battleship_t4',
  nest: 'carrier_t1', hive: 'carrier_t2', swarm: 'carrier_t3', armada: 'carrier_t4',
  titan: 'dreadnought_t1', colossus: 'dreadnought_t2', behemoth: 'dreadnought_t3', sovereign: 'dreadnought_t4',
  mule: 'hauler_t1', nomad: 'hauler_t2', caravan: 'hauler_t3', mammoth: 'hauler_t4',
};

const ALL_CLASSES: ShipClassType[] = [
  'corvette', 'frigate', 'destroyer', 'cruiser',
  'battleship', 'carrier', 'dreadnought', 'hauler',
];

const SHIPS_CANONICAL: Record<string, ShipClass> = {};
for (const cls of ALL_CLASSES) {
  for (const tier of [1, 2, 3, 4] as ShipTier[]) {
    const sc = buildShipClass(cls, tier);
    SHIPS_CANONICAL[sc.id] = sc;
  }
}

// Legacy alias map → same object references
export const SHIPS: Record<string, ShipClass> = { ...SHIPS_CANONICAL };
for (const [legacy, canonical] of Object.entries(LEGACY_SHIP_IDS)) {
  SHIPS[legacy] = SHIPS_CANONICAL[canonical];
}

/** Planet landing is class-based, not tier-based. Capital hulls stay in space. */
export const PLANET_LANDING_CLASSES: readonly ShipClassType[] = [
  'corvette', 'frigate', 'destroyer', 'cruiser', 'hauler',
];
export function canShipLand(shipClassId: string): boolean {
  const ship = Object.prototype.hasOwnProperty.call(SHIPS, shipClassId) ? SHIPS[shipClassId] : undefined;
  return ship !== undefined && PLANET_LANDING_CLASSES.includes(ship.class);
}

// Export legacy ship constants too (sparrow, swift, ...) for backward-compat
// with any code that imports them directly. They all point at canonical entries.
export const SHIP_SPARROW = SHIPS_CANONICAL.corvette_t1;
export const SHIP_SWIFT = SHIPS_CANONICAL.corvette_t2;
export const SHIP_FALCON = SHIPS_CANONICAL.corvette_t3;
export const SHIP_PEREGRINE = SHIPS_CANONICAL.corvette_t4;
export const SHIP_CORSAIR = SHIPS_CANONICAL.frigate_t1;
export const SHIP_RAIDER = SHIPS_CANONICAL.frigate_t2;
export const SHIP_STALKER = SHIPS_CANONICAL.frigate_t3;
export const SHIP_PHANTOM = SHIPS_CANONICAL.frigate_t4;
export const SHIP_SENTINEL = SHIPS_CANONICAL.destroyer_t1;
export const SHIP_GUARDIAN = SHIPS_CANONICAL.destroyer_t2;
export const SHIP_WARDEN = SHIPS_CANONICAL.destroyer_t3;
export const SHIP_VINDICATOR = SHIPS_CANONICAL.destroyer_t4;
export const SHIP_PIONEER = SHIPS_CANONICAL.cruiser_t1;
export const SHIP_VOYAGER = SHIPS_CANONICAL.cruiser_t2;
export const SHIP_PATHFINDER = SHIPS_CANONICAL.cruiser_t3;
export const SHIP_ODYSSEY = SHIPS_CANONICAL.cruiser_t4;
export const SHIP_BULWARK = SHIPS_CANONICAL.battleship_t1;
export const SHIP_FORTRESS = SHIPS_CANONICAL.battleship_t2;
export const SHIP_JUGGERNAUT = SHIPS_CANONICAL.battleship_t3;
export const SHIP_LEVIATHAN = SHIPS_CANONICAL.battleship_t4;
export const SHIP_NEST = SHIPS_CANONICAL.carrier_t1;
export const SHIP_HIVE = SHIPS_CANONICAL.carrier_t2;
export const SHIP_SWARM = SHIPS_CANONICAL.carrier_t3;
export const SHIP_ARMADA = SHIPS_CANONICAL.carrier_t4;
export const SHIP_TITAN = SHIPS_CANONICAL.dreadnought_t1;
export const SHIP_COLOSSUS = SHIPS_CANONICAL.dreadnought_t2;
export const SHIP_BEHEMOTH = SHIPS_CANONICAL.dreadnought_t3;
export const SHIP_SOVEREIGN = SHIPS_CANONICAL.dreadnought_t4;
export const SHIP_MULE = SHIPS_CANONICAL.hauler_t1;
export const SHIP_NOMAD = SHIPS_CANONICAL.hauler_t2;
export const SHIP_CARAVAN = SHIPS_CANONICAL.hauler_t3;
export const SHIP_MAMMOTH = SHIPS_CANONICAL.hauler_t4;

// ============================================
// Lookup helpers
// ============================================
export function resolveShipId(id: string): string {
  return LEGACY_SHIP_IDS[id] ?? id;
}

export function getShipById(id: string): ShipClass | undefined {
  return SHIPS[id] ?? SHIPS_CANONICAL[LEGACY_SHIP_IDS[id]];
}

export function getShipsByTier(tier: ShipTier): ShipClass[] {
  return Object.values(SHIPS_CANONICAL).filter(ship => ship.tier === tier);
}

export function getShipsByCategory(category: ShipClass['category']): ShipClass[] {
  return Object.values(SHIPS_CANONICAL).filter(ship => ship.category === category);
}

export function getShipsByClass(shipClass: ShipClassType): ShipClass[] {
  return Object.values(SHIPS_CANONICAL).filter(ship => ship.class === shipClass);
}
